import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import type { Dayjs } from 'dayjs';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';

/**
 * Format tanggal default Booku V2 — `DD/MM/YYYY`.
 *
 * Sesuai konvensi Indonesia & Booku Lama. Bisa di-override per call site
 * via prop `format`.
 */
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

/**
 * Props {@link DatePickerBox} — wrapper Antd DatePicker dengan opinionated defaults.
 */
export interface DatePickerBoxProps extends Omit<DatePickerProps, 'size' | 'onChange' | 'multiple' | 'value' | 'defaultValue' | 'onOk'> {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu. */
  size?: DatePickerProps['size'];
  /**
   * Handler perubahan tanggal — dipersempit ke single-date variant.
   * Booku V2 tidak memakai `multiple` mode pada DatePicker, jadi callback
   * hanya menerima `Dayjs | null` (bukan `Dayjs[]`).
   */
  onChange?: (date: Dayjs | null, dateString: string | string[] | null) => void;
  /** Nilai single-date (tanpa support multiple). */
  value?: Dayjs | null;
  /** Default value single-date (tanpa support multiple). */
  defaultValue?: Dayjs | null;
  /** Handler tombol OK (panel waktu) — single-date variant. */
  onOk?: (date: Dayjs) => void;
}

/**
 * BASE: Date picker.
 *
 * Sifat universal:
 * - Density mengikuti `context` (page=small, form/drawer=middle).
 * - Width default 100% (mengikuti container Form.Item / Col).
 * - Format default `DD/MM/YYYY` (konvensi Indonesia & Booku Lama).
 * - Class `booku-control` → vertical alignment seragam dengan komponen lain.
 *
 * Pemakaian:
 * ```tsx
 * <DatePickerBox value={tanggal} onChange={setTanggal} />
 * <DatePickerBox value={tanggal} onChange={setTanggal} format="YYYY-MM-DD" />
 * ```
 *
 * Untuk range: pakai {@link DateRangePickerBox}.
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
export const DatePickerBox = forwardRef<
  React.ComponentRef<typeof DatePicker>,
  DatePickerBoxProps
>(function DatePickerBox(
  { context = 'form', size, format, style, className, onChange, ...rest },
  ref
) {
  const resolvedSize = size ?? controlSizeForContext(context);
  // AntD's DatePicker onChange union: (Dayjs | Dayjs[] | null). Karena kita
  // tidak memakai `multiple` mode, narrow ke single-date variant.
  const handleChange: DatePickerProps['onChange'] = onChange
    ? (date, dateString) => {
        const single = Array.isArray(date) ? null : date;
        onChange(single, dateString);
      }
    : undefined;
  return (
    <DatePicker
      ref={ref}
      size={resolvedSize}
      format={format ?? DEFAULT_DATE_FORMAT}
      className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
      style={{ width: '100%', ...style }}
      onChange={handleChange}
      {...rest}
    />
  );
});
